import React, { memo, useMemo, useState, useCallback, useEffect } from 'react'
import { Box, Flex, Text, Avatar, useDisclosure } from '@chakra-ui/core'
import { RootState, Task } from 'constants/interfaces'
import { Post } from 'redux/reducers/feed'
import useAvatar from 'hooks/useAvatar'
import { useUserName } from 'hooks/useUserName'
import colors from 'constants/colors'
import { X, CircleCheck, Megaphone } from 'lucide-react'
import { css } from '@emotion/core'
import styled from '@emotion/styled'
import { useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'
import { NoFocusButton } from 'components/CommonComponents/NoFocusButton'
import { PostOverviewModal } from '../PostOverviewModal'
import { TaskOverviewModal } from 'components/TasksV2/TasksOverview/TaskOverviewModal'
import { AllItemsView } from './AllItemsView'
import { TasksFilteredView } from './TasksFilteredView'
import { PostsFilteredView } from './PostsFilteredView'
import { OwnerTaskInsightsView } from './OwnerTaskInsightsView'
import { isEqual } from 'lodash'
import { getDropShadowFilter } from './utils'
import useLocaleText from 'components/useLocaleText'

interface PersonalViewDrawerProps {
  isOpen: boolean
  onClose: () => void
  userId: string
  tasks: Task[]
  completedTasks: Task[]
  uncompletedTasks: Task[]
  posts: Post[]
  readPosts: Post[]
  ownerTasks?: Task[]
  isSelf?: boolean
  colorIndex?: number
}

const SidebarContainer = styled(Box)<{ isOpen: boolean; isRtl: boolean }>`
  width: ${({ isOpen }) => (isOpen ? 'clamp(320px, 34vw, 560px)' : '0px')};
  max-width: ${({ isOpen }) => (isOpen ? 'clamp(320px, 34vw, 560px)' : '0px')};
  height: calc(100vh - 65px);
  background: white;
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: ${({ isOpen }) => (isOpen ? '0 6px 32px rgba(0,0,0,.14)' : 'none')};
  transition: width 0.3s ease, max-width 0.3s ease, box-shadow 0.3s ease;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
`

const CloseBtn = styled(Box)`
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`

export const PersonalViewDrawer = memo(
  ({
    isOpen,
    onClose,
    userId,
    tasks,
    completedTasks,
    uncompletedTasks,
    posts,
    readPosts,
    ownerTasks = [],
    isSelf = false,
    colorIndex = 0,
  }: PersonalViewDrawerProps) => {
    const avatar = useAvatar(userId)
    const userName = useUserName(userId)
    const theme = useSelector((state: RootState) => state.general.theme)
    const isRtl = useSelector(isRtlSelector)
    const shadowFilter = useMemo(() => getDropShadowFilter(colorIndex, 'subtle'), [colorIndex])

    const [activeFilter, setActiveFilter] = useState<'tasks' | 'updates' | null>(null)
    const [selectedPost, setSelectedPost] = useState<Post | null>(null)
    const [selectedTaskID, setSelectedTaskID] = useState<string | null>(null)
    const [selectedOwnerTask, setSelectedOwnerTask] = useState<Task | null>(null)
    const {
      isOpen: isPostOverviewModalOpen,
      onOpen: onOpenPostOverviewModal,
      onClose: onClosePostOverviewModal,
    } = useDisclosure()

    const allPosts = useSelector((state: RootState) => state.feed.posts, isEqual)

    const allDrawerTasks = useMemo(
      () => [...tasks, ...completedTasks, ...uncompletedTasks],
      [tasks, completedTasks, uncompletedTasks]
    )

    const selectedTask = useMemo(
      () => (selectedTaskID ? allDrawerTasks.find((t) => t.tid === selectedTaskID) || null : null),
      [selectedTaskID, allDrawerTasks]
    )

    const all_updates = useLocaleText('all_updates')
    const all_tasks = useLocaleText('all_tasks')

    const handleOpenSelectedPost = useCallback(
      (postID: string) => {
        const post = allPosts.find((p) => p.post_id === postID)
        setSelectedPost(post || null)
        if (post) {
          onOpenPostOverviewModal()
        }
      },
      [onOpenPostOverviewModal, allPosts]
    )

    const handleFilterClick = useCallback(
      (filter: 'tasks' | 'updates') => {
        setActiveFilter(activeFilter === filter ? null : filter)
      },
      [activeFilter]
    )

    // Reset activeFilter and selectedTaskID when drawer closes or userId changes
    useEffect(() => {
      if (!isOpen) {
        setActiveFilter(null)
        setSelectedTaskID(null)
        setSelectedOwnerTask(null)
      }
    }, [isOpen])

    useEffect(() => {
      setActiveFilter(null)
      setSelectedTaskID(null)
      setSelectedOwnerTask(null)
    }, [userId])

    return (
      <SidebarContainer isOpen={isOpen} isRtl={isRtl}>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {selectedOwnerTask ? (
            <OwnerTaskInsightsView task={selectedOwnerTask} onBack={() => setSelectedOwnerTask(null)} />
          ) : (
            <>
              <Flex
                p={{ base: '10px', md: '15px', lg: '20px' }}
                minH="200px"
                flexDirection="column"
                css={css`
                  background: linear-gradient(135deg, #e8f4f8 0%, #ffe7f0 100%);
                `}
                position="relative"
                flexShrink={0}
              >
                <CloseBtn
                  as="button"
                  aria-label="Close personal view"
                  onClick={onClose}
                  style={{
                    position: 'absolute',
                    top: 15,
                    ...(isRtl ? { left: 15 } : { right: 15 }),
                  }}
                >
                  <X size={20} />
                </CloseBtn>

                <Flex alignItems="center" flexDirection="column" justifyContent="center" w="100%">
                  <Box position="relative">
                    <Avatar
                      src={avatar}
                      w="124px"
                      h="124px"
                      borderRadius="full"
                      border="4px solid white"
                      css={css`
                        filter: ${shadowFilter};
                      `}
                    />
                  </Box>
                  <Text fontSize="24px" fontWeight="bold" color={colors.blackLight} mt="16px" mb="20px">
                    {userName || userId}
                  </Text>
                </Flex>
                <Flex style={{ gap: 10 }}>
                  <NoFocusButton
                    flex="1"
                    bg={activeFilter === 'tasks' ? theme?.elementsColor : 'white'}
                    color={activeFilter === 'tasks' ? 'white' : colors.blackLight}
                    fontWeight="bold"
                    borderRadius="20px"
                    borderColor={colors.greyBorder || colors.greyLight3}
                    isDisabled={tasks.length === 0 && completedTasks.length === 0}
                    onClick={() => (tasks.length > 0 || completedTasks.length > 0) && handleFilterClick('tasks')}
                    position="relative"
                    style={{ columnGap: 8 }}
                    _hover={{ opacity: 0.8 }}
                    _active={{ opacity: 0.8 }}
                  >
                    <CircleCheck size={18} />
                    <Text>{all_tasks}</Text>
                  </NoFocusButton>

                  <NoFocusButton
                    flex="1"
                    bg={activeFilter === 'updates' ? theme?.elementsColor : 'white'}
                    color={activeFilter === 'updates' ? 'white' : colors.blackLight}
                    fontWeight="bold"
                    borderRadius="20px"
                    borderColor={colors.greyBorder || colors.greyLight3}
                    isDisabled={posts.length === 0 && readPosts.length === 0}
                    onClick={() => (posts.length > 0 || readPosts.length > 0) && handleFilterClick('updates')}
                    position="relative"
                    style={{ columnGap: 8 }}
                    _hover={{ opacity: 0.8 }}
                    _active={{ opacity: 0.8 }}
                  >
                    <Megaphone size={18} />
                    <Text>{all_updates}</Text>
                  </NoFocusButton>
                </Flex>
              </Flex>

              <Box p="15px" overflowY="auto" overflowX="hidden" flex="1" bg="#FAFAFA">
                {activeFilter === null && (
                  <AllItemsView
                    tasks={tasks}
                    posts={posts}
                    readPosts={readPosts}
                    ownerTasks={ownerTasks}
                    isSelf={isSelf}
                    userId={userId}
                    handleOpenSelectedPost={handleOpenSelectedPost}
                    selectedTaskID={selectedTaskID}
                    onTaskSelect={setSelectedTaskID}
                    onTaskClose={() => setSelectedTaskID(null)}
                    onOwnerTaskClick={setSelectedOwnerTask}
                  />
                )}

                {activeFilter === 'tasks' && (
                  <TasksFilteredView
                    uncompletedTasks={[...tasks, ...uncompletedTasks]}
                    completedTasks={completedTasks}
                    userId={userId}
                    selectedTaskID={selectedTaskID}
                    onTaskSelect={setSelectedTaskID}
                    onTaskClose={() => setSelectedTaskID(null)}
                  />
                )}

                {activeFilter === 'updates' && (
                  <PostsFilteredView
                    handleOpenSelectedPost={handleOpenSelectedPost}
                    userId={userId}
                    unreadPosts={posts}
                    readPosts={readPosts}
                  />
                )}
              </Box>
            </>
          )}
        </Box>

        {selectedTask && <TaskOverviewModal task={selectedTask} onClose={() => setSelectedTaskID(null)} />}

        {isPostOverviewModalOpen && selectedPost && (
          <PostOverviewModal isOpen={isPostOverviewModalOpen} onClose={onClosePostOverviewModal} post={selectedPost} />
        )}
      </SidebarContainer>
    )
  }
)
